import { useState } from "react";
import { useNavigate } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthProvider";
import { FactorPicker } from "@/auth/FactorPicker";
import { pickInitialFactorId, rememberFactorId, type TotpFactor } from "@/auth/mfaFactors";

interface MFAVerificationProps {
  factors: TotpFactor[];
  onSuccess: () => void;
  onCancel?: () => void;
}

export const MFAVerification = ({ factors, onSuccess, onCancel }: MFAVerificationProps) => {
  const [factorId, setFactorId] = useState(() => pickInitialFactorId(factors));
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { verifyMFA } = useAuth();
  const navigate = useNavigate();

  const handleVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrorMessage("Please enter a 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      setErrorMessage(null);
      const { error } = await verifyMFA({ factorId, code: verificationCode });

      if (error) {
        setErrorMessage("Invalid verification code. Please try again.");
        setVerificationCode("");
        return;
      }

      rememberFactorId(factorId);
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && verificationCode.length === 6) {
      handleVerification();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            {factors.length > 1
              ? "Choose your device, then enter its 6-digit code"
              : "Enter the 6-digit code from your authenticator app"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <FactorPicker factors={factors} value={factorId} onChange={setFactorId} />
            <Label htmlFor="mfa-code">Verification Code</Label>
            <Input
              id="mfa-code"
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (errorMessage) setErrorMessage(null);
              }}
              onKeyDown={handleKeyDown}
              maxLength={6}
              className="text-center text-lg tracking-widest"
              autoComplete="one-time-code"
              autoFocus
            />
            {errorMessage && <p className="text-sm text-destructive mt-1">{errorMessage}</p>}
          </div>

          <Button onClick={handleVerification} disabled={loading || verificationCode.length !== 6} className="w-full">
            {loading ? "Verifying..." : "Verify Code"}
          </Button>

          <Button onClick={() => (onCancel ? onCancel() : navigate("/auth"))} variant="outline" className="w-full">
            Return to Sign In
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
